import { GetterTree, ActionTree } from 'vuex';
import { CreateTypedStoreProxy } from 'vuex-typed-store';
import vuestore, { store } from '../../../store';
import { areaSaggioPiantaCampioneStore } from '@/modules/AreaSaggioPiantaCampione/store/areaSaggioPiantaCampione.store';

export interface ReportStoreModel {
  logarithmicRegression: report.regression
}

export interface ReportStoreActions {
  init();
  set_logarithmic_regression: (areaSaggioId) => Promise<void>;
}

export interface ReportStoreGetters {
  regression: () => report.regression;
}

export const ReportStore = {
  PREFIX: 'report',
  namespaced: true,
  state: {
    logarithmicRegression: null
  },
  mutations: {
    set_logarithmic_regression(state: ReportStoreModel, regression: report.regression) {
      state.logarithmicRegression = regression
    },
  },
  getters: {
    regression: (state) => () => state.logarithmicRegression
  } as GetterTree<ReportStoreModel, ReportStoreGetters>,
  actions: {
    async init(context) {
      const areaSaggioId = store.getters.navigation.selected("areaSaggio");
      const value = await getValuesOfLogarithmicExpression(areaSaggioId);
      context.commit('set_logarithmic_regression', value);
    },
    async set_logarithmic_regression(context, areaSaggioId: string) {
      const value = await getValuesOfLogarithmicExpression(areaSaggioId);
      context.commit('set_logarithmic_regression', value);
    }
  } as ActionTree<ReportStoreModel, ReportStoreActions>
}

export const reportStore = CreateTypedStoreProxy<ReportStoreModel, ReportStoreActions, ReportStoreGetters>(vuestore, ReportStore.PREFIX);

async function getValuesOfLogarithmicExpression(areaSaggioId: string): Promise<report.regression> {
  let coordinates = [];
  if (!areaSaggioId)
    return null;
  const pianteCampione = (await areaSaggioPiantaCampioneStore.getters.getAll())
     .filter(i => i.areaSaggioId === areaSaggioId);

  coordinates = pianteCampione.map(m => {
    return [m.diametro || 0, m.altezza || 0];
  })
  return logarithmicRegression(coordinates);

}

function logarithmicRegression(data, options: { order: number, precision: number } = { order: 2, precision: 2 }): report.regression {
  const sum = [0, 0, 0, 0];
  const len = data.length;

  for (let n = 0; n < len; n++) {
    if (data[n][1] !== null) {
      sum[0] += Math.log(data[n][0]);
      sum[1] += data[n][1] * Math.log(data[n][0]);
      sum[2] += data[n][1];
      sum[3] += (Math.log(data[n][0]) ** 2);
    }
  }

  const a = ((len * sum[1]) - (sum[2] * sum[0])) / ((len * sum[3]) - (sum[0] * sum[0]));
  const coeffB = round(a, options.precision);
  const coeffA = round((sum[2] - (coeffB * sum[0])) / len, options.precision);
  
  
  const fn = x => round(round(coeffA + (coeffB * Math.log(x)), options.precision), options.precision);
  const points = data.map(point => fn(point[0]));

  return {
    points,
    fn: fn,
    equation: [coeffA, coeffB],
    string: `y = ${coeffA} + ${coeffB} ln(x)`,
    r2: round(determinationCoefficient(data, points), options.precision),
  };

  function determinationCoefficient(data, results) {
    const predictions = [];
    const observations = [];

    data.forEach((d, i) => {
      if (d[1] !== null) {
        observations.push(d);
        predictions.push(results[i]);
      }
    });

    const sum = observations.reduce((a, observation) => a + observation[1], 0);
    const mean = sum / observations.length;

    const ssyy = observations.reduce((a, observation) => {
      const difference = observation[1] - mean;
      return a + (difference * difference);
    }, 0);

    const sse = observations.reduce((accum, observation, index) => {
      const prediction = predictions[index];
      const residual = observation[1] - prediction[1];
      return accum + (residual * residual);
    }, 0);

    return 1 - (sse / ssyy);
  }

  function round(number, precision) {
    const factor = 10 ** precision;
    return Math.round(number * factor) / factor;
  }
}
